import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes } from 'react-router-dom';

import AppointmentPage from '../Component/BookingJourney/AppointmentPageV2';
import PatientLoginPage from '../Component/PatientAccount/pages/PatientLoginPage';
import { PatientAuthProvider } from '../Component/PatientAccount/context/PatientAuthContext';
import PatientProtectedRoute from '../Component/PatientAccount/PatientProtectedRoute';
import { IS_APP_MODE } from '../Component/PatientAccount/appMode';

const ReschedulePage = lazy(() => import('../Component/BookingJourney/reschedule'));
const ReschedulePageV2 = lazy(() => import('../Component/BookingJourney/rescheduleV2'));
const RescheduleTrigger = lazy(() => import('./RescheduleTriggle'));
const GoogleCalander = lazy(() => import('../Component/BookingJourney/GoogleCalander'));
const GoogleCalanderV2 = lazy(() => import('../Component/BookingJourney/GoogleCalanderV2'));
const BookingConfirmation = lazy(() => import('../Component/Dashboard/BookingConfirmation'));
const FollowUpPage = lazy(() => import('../Component/FollowUp/FollowUpPage'));
const ResetPassword = lazy(() => import('../Component/PatientAccount/pages/ResetPassword'));
const PatientOtpPage = lazy(() => import('../Component/PatientAccount/pages/PatientOtpPage'));
const VerifyEmailPage = lazy(() => import('../Component/PatientAccount/pages/VerifyEmailPage'));
const PatientDashboardPage = lazy(() => import('../Component/PatientAccount/pages/PatientDashboardPage'));
const PatientProfilePage = lazy(() => import('../Component/PatientAccount/pages/PatientProfilePage'));
const PatientAppointmentsPage = lazy(() => import('../Component/PatientAccount/pages/PatientAppointmentsPage'));
const PatientServicesPage = lazy(() => import('../Component/PatientAccount/pages/PatientServicesPage'));
const PatientClinicsPage = lazy(() => import('../Component/PatientAccount/pages/PatientClinicsPage'));
const PatientPaymentMethodsPage = lazy(() => import('../Component/PatientAccount/pages/PatientPaymentMethodsPage'));
const PatientBookingPage = lazy(() => import('../Component/PatientAccount/pages/PatientBookingPage'));
const PatientReschedulePage = lazy(() => import('../Component/PatientAccount/pages/PatientReschedulePage'));
const PatientRescheduleShellPage = lazy(() => import('../Component/PatientAccount/pages/PatientRescheduleShellPage'));
const KlarnaReturn = lazy(() => import('../Component/BookingJourney/StepComponents/KlarnaReturn'));
const DevPreviewPTV2 = lazy(() => import('../Component/BookingJourney/DevPreviewPTV2')); // TEMP: remove after manual verification

const LoadingSpinner = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: '#fafafa',
        zIndex: 9999
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e0e0e0',
            borderTop: '3px solid #008385',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }} />
        <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);

const hardcodedProps = {
    "getAllHealthScreenData": {
        "tempData": {
            "product": "General GP consultation",
            "data": [
                {
                    "key": "Duration",
                    "data": "15 minutes"
                },
                {
                    "key": "languageData",
                    "data": "Arabic"
                },
                {
                    "key": "Gender",
                    "data": "Male"
                }
            ],
            "date": "14 January, 2025",
            "time": "10:30",
            "name": "Oxford Street",
            "address": "6 Newburgh Street, Carnaby, London, W1F 7RQ"
        }
    },
    "showAppoinmentData": {
        "duration": 1,
        "language": "English",
        "gender": "no_preference",
        "price": "£  40.00",
        "type": "BookingForm",
        "slot": {
            "date": "Fri, 25 Jul2025",
            "info": {
                "start_time": "16:15"
            },
            "clinicData": {
                "id": 47,
                "name": "Hampstead (London Doctors Clinic)",
                "address": "213 Haverstock Hill, Belsize Park, London, NW3 4QP",
                "latitude": "-.1692526",
                "longitude": "51.5509691"
            }
        },
        "clinic": {
            "productCategory": {
                "title": "GP appointment",
                "product_category_id": 1
            },
            "non_gp_product_id": null
        }
    },
    "data": {

    },
    "BookedAppointmentPayload": {
        "success": true,
        "message": "Appointment booked successfully.",
        "data": {
            "patient_id": 271,
            "room_id": "247",
            "duration_id": 1,
            "product_id": null,
            "preferred_gender": "no_preference",
            "appointment_type_int": 1,
            "charges": "40.00",
            "start_time": "2025-07-25 16:15",
            "end_time": "2025-07-25 16:30",
            "booked_on": "2025-07-25T14:20:03.045316Z",
            "notes": "sdf",
            "clinic_id": 47,
            "product_category_id": 1,
            "cancellations_rescheduling_allowed": 1,
            "appointment_reason_id": 0,
            "status": 1,
            "appt_source": 1,
            "created_by": null,
            "updated_by": null,
            "parent_id": "AP-0000000230",
            "updated_at": "2025-07-25T14:20:03.046000Z",
            "created_at": "2025-07-25T14:20:03.046000Z",
            "appointment_id": 315,
            "latitude": "-.1692526",
            "longitude": "51.5509691"
        },
        // "google_calendar": {
        //     "requires_auth": true,

        //     "auth_url": "https://accounts.google.com//o//oauth2//v2//auth?response_type=code&access_type=offline&client_id=360487503727-ib23qh79b7d2br63bqlfu8ptqp7qgtsn.apps.googleusercontent.com&redirect_uri=https%3A%2F%2Fpbldcbe.webdezign.uk%2Fgoogle-calendar%2Fcallback&state=315&scope=https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fcalendar&prompt=consent"
        // }
        "google_calendar": {
            "requires_auth": false
        }
    }
}

const MainRouter = (props) => {

    const isWidgetMode = process.env.REACT_APP_WIDGET_MODE === 'true';
    const isPatientMode = process.env.REACT_APP_PATIENT_MODE === 'true';

    // Auto-logout patient on app boot if not in patient mode
    useEffect(() => {
        if (!isPatientMode) {
            const hasToken = localStorage.getItem('patient_auth_token');
            if (hasToken) {
                ['patient_auth_token', 'patient_account_info', 'patient_last_active_time'].forEach(k => localStorage.removeItem(k));
                [
                    'patient_otp_mobile',
                    'patient_otp_message_id',
                    'patient_login_email',
                    'patient_redirect_after_login',
                    'pa_prefill',
                ].forEach(k => sessionStorage.removeItem(k));
            }
        }
    }, [isPatientMode]);

    // ── Klarna return detection (widget mode only) ────────────────────────────
    // Stripe redirects back to the same host page with ?payment_intent=&redirect_status=
    // appended. Detect those params here and route the MemoryRouter directly to
    // /klarna-return so KlarnaReturn can read them from window.location.search.
    const klarnaParams = (() => {
        if (!isWidgetMode) return null;
        const sp = new URLSearchParams(window.location.search);
        return sp.get('payment_intent') && sp.get('redirect_status') ? sp : null;
    })();
    const initialPath = klarnaParams
        ? `/klarna-return?${klarnaParams.toString()}`
        : (props.initialPath || '/');

    // console.log("MainRouter product_category_id:", props);
    const encrypted = props.resheduleKey || "";
    const stateCode = props.stateCode || "";
    const authCode = props.authCode || "";
    const productId = props.productId || "";
    const clinicId = props.clinicId || "";
    const hashCode = props.hashCode || "";
    const partnerId = props?.partnerId || "";
    const hideAdditionalPreferences = props?.hideAdditionalPreferences || props.clinicId ? true : false;
    return (
        <>
            {isWidgetMode ? (
                <MemoryRouter initialEntries={[initialPath]}>
                    <Suspense fallback={<LoadingSpinner />}>
                        <Routes>
                            <Route path="/" element={<AppointmentPage productCategoryId={props.productCategoryId} productId={productId} clinicId={clinicId} hashCode={hashCode} partnerId={partnerId} hideAdditionalPreferences={hideAdditionalPreferences} />} />
                            <Route path="/booking_confirmation" element={<BookingConfirmation {...hardcodedProps} />} />
                            {/* <Route path="/book" element={<UnifiedAppointmentType />} />
                                    <Route path="/gp-journey" element={<AppointmentPage />} />
                                    <Route path="/gp-book-journey" element={<BookingJourney />} />
                                    <Route path="/health-booking" element={<Hbooking />} />
                                    <Route path="/health-journey" element={<HJourney />} /> */}
                            <Route path={`/view-appointment`} element={<ReschedulePageV2 encryptedId={encrypted} hideAdditionalPreferences={hideAdditionalPreferences} />} />
                            <Route path="/google-calendar" element={<GoogleCalanderV2 stateCode={stateCode} authCode={authCode} />} />
                            <Route path="/view-appointment-trigger" element={<RescheduleTrigger />} />
                            {/* IOO-291 – Follow-Up Appointment */}
                            <Route path="/followup-appointment" element={<FollowUpPage />} />
                            <Route path="/klarna-return" element={<KlarnaReturn />} />
                        </Routes>
                    </Suspense>
                </MemoryRouter>
            ) : (
                <PatientAuthProvider>
                    <BrowserRouter basename={isPatientMode ? process.env.PUBLIC_URL : undefined}>
                        <Suspense fallback={<LoadingSpinner />}>
                            <Routes>
                                {/* IOO-225 – Patient mode: root renders PatientLoginPage directly */}
                                {isPatientMode
                                    ? <Route path="/" element={<PatientLoginPage />} />
                                    : <Route path="/" element={<AppointmentPage />} />
                                }
                                <Route path="/booking_confirmation" element={<BookingConfirmation {...hardcodedProps} />} />
                                {/* <Route path="/book" element={<UnifiedAppointmentType />} />
                                <Route path="/gp-journey" element={<AppointmentPage />} />
                                <Route path="/gp-book-journey" element={<BookingJourney />} />
                                <Route path="/health-booking" element={<Hbooking />} />
                                <Route path="/health-journey" element={<HJourney />} /> */}
                                <Route path="/view-appointment/:encryptedId" element={<ReschedulePage hideAdditionalPreferences={hideAdditionalPreferences} />} />
                                <Route path="/google-calendar/:code" element={<GoogleCalander />} />
                                <Route path="/view-appointment-trigger" element={<RescheduleTrigger />} />
                                {/* IOO-291 – Follow-Up Appointment */}
                                <Route path="/followup-appointment" element={<FollowUpPage />} />
                                {/* IOO-225 – Patient Personal Account (standalone, BrowserRouter only) */}
                                <Route path={isPatientMode ? "/login" : "/patient/login"} element={isPatientMode ? <Navigate to="/" replace /> : <PatientLoginPage />} />
                                <Route path={isPatientMode ? "/verify" : "/patient/verify"} element={<PatientOtpPage />} />
                                {/* IOO-372 – Post-checkout account sign-up: email-journey landing page */}
                                <Route path={isPatientMode ? "/verifyemail" : "/patient/verifyemail"} element={<VerifyEmailPage />} />
                                <Route path={isPatientMode ? "/dashboard" : "/patient/dashboard"} element={<PatientProtectedRoute><PatientDashboardPage /></PatientProtectedRoute>} />
                                <Route path={isPatientMode ? "/profile" : "/patient/profile"} element={<PatientProtectedRoute><PatientProfilePage /></PatientProtectedRoute>} />
                                <Route path={isPatientMode ? "/appointments" : "/patient/appointments"} element={<PatientProtectedRoute><PatientAppointmentsPage /></PatientProtectedRoute>} />
                                <Route path={isPatientMode ? "/payment-methods" : "/patient/payment-methods"} element={IS_APP_MODE ? <Navigate to="/profile" replace /> : <PatientProtectedRoute><PatientPaymentMethodsPage /></PatientProtectedRoute>} />
                                {/* App (WebView) build only – new bottom-nav tabs */}
                                {IS_APP_MODE && <Route path="/services" element={<PatientProtectedRoute><PatientServicesPage /></PatientProtectedRoute>} />}
                                {IS_APP_MODE && <Route path="/clinics" element={<PatientProtectedRoute><PatientClinicsPage /></PatientProtectedRoute>} />}
                                {/* IOO-225 – Patient-context booking (stays inside patient shell, not guest) */}
                                <Route path={isPatientMode ? "/book" : "/patient/book"} element={<PatientProtectedRoute><PatientBookingPage /></PatientProtectedRoute>} />
                                {/* IOO-225 – Patient reschedule journey (3-step dummy flow) */}
                                <Route path={isPatientMode ? "/reschedule" : "/patient/reschedule"} element={<PatientProtectedRoute><PatientReschedulePage /></PatientProtectedRoute>} />
                                {/* IOO-225 – view-appointment?key= links: real ReschedulePageV2 inside patient shell */}
                                <Route path="/view-appointment" element={<PatientProtectedRoute><PatientRescheduleShellPage /></PatientProtectedRoute>} />
                                <Route path="/book-an-appointment/klarna-return" element={<KlarnaReturn />} />
                                <Route path="/reset-password" element={<ResetPassword />} />
                                <Route path="/dev-ptv2-preview" element={<DevPreviewPTV2 />} /> {/* TEMP: remove after manual verification */}
                            </Routes>
                        </Suspense>
                    </BrowserRouter>
                </PatientAuthProvider>
            )}
        </>
    );
};

export default MainRouter
